<script setup lang="ts">
import { Check, Search, X } from 'lucide-vue-next';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref } from 'vue';

import type { Selection } from '@/types/configuration';
import type { TaxonomyType } from '@/types/filters';

import { useEditor } from '@/composables/useEditor';
import { useMetricsStore } from '@/stores/useMetricsStore';
import { useProductsStore } from '@/stores/useProductsStore';

type SelectionFilter = {
  id: string;
  name: string;
  count: number;
};

const { configToFilter } = useEditor();

const metricsStore = useMetricsStore();
const { places, themes, audiences, internals, types } = storeToRefs(metricsStore);

const productsStore = useProductsStore();
const { totalResults } = storeToRefs(productsStore);
const { fetchProductsList } = productsStore;

const config = defineModel<Selection>('config', {
  required: true,
});

const search = ref<Map<string, string>>(new Map());
// Xavier Hernandez - Disabled updating filters based on client ticket
// let debounce = 0

const categories = computed<Record<TaxonomyType, SelectionFilter[]>>(() => {
  const formatText = (text: string) => text.replace(/_/g, ' ').toLowerCase();
  const filterSearch = (type: TaxonomyType, filters: SelectionFilter[]) => {
    let searchText = search.value.get(type);
    if (searchText == null) {
      return filters;
    }
    searchText = formatText(searchText);
    return filters.filter(it => formatText(it.name).includes(searchText));
  };

  return {
    THEMES: filterSearch('THEMES', themes.value),
    TYPES: filterSearch('TYPES', types.value),
    AUDIENCES: filterSearch('AUDIENCES', audiences.value),
    PLACES: filterSearch('PLACES', places.value),
    INTERNALS: filterSearch('INTERNALS', internals.value),
  };
});

function setFilter(filterId: string, type: TaxonomyType) {
  const taxonomie = config.value?.taxonomies.find(it => it.type === type)?.value;
  if (taxonomie == null) {
    config.value.taxonomies = [
      ...config.value.taxonomies,
      {
        type,
        value: [filterId],
      },
    ];
  }
  else {
    const alreadyActive = taxonomie.includes(filterId);
    if (taxonomie.length === 1 && alreadyActive) {
      config.value.taxonomies = config.value.taxonomies.filter(it => it.type !== type); // Remove type because it will be empty
    }
    else if (alreadyActive) {
      config.value.taxonomies = [...config.value.taxonomies.filter(it => it.type !== type), {
        type,
        value: [...taxonomie.filter(it => it !== filterId)],
      }]; // Remove selected filter
    }
    else {
      config.value.taxonomies = [...config.value.taxonomies.filter(it => it.type !== type), {
        type,
        value: [...taxonomie, filterId],
      }]; // Add filter to existing type
    }
  }
}

function isSelected(filter: SelectionFilter, type: TaxonomyType) {
  const taxonomie = config.value.taxonomies.find(it => it.type === type)?.value;
  if (taxonomie == null) {
    return false;
  }
  return taxonomie.includes(filter.id);
}

function getFilterText(filterId: string, type: TaxonomyType) {
  return getFilterFromId(filterId, type, false)?.name ?? 'Filtre inconnu';
}

function getFilterFromId(filterId: string, type: TaxonomyType, throwError: boolean = true) {
  const result = categories.value[type].find(it => it.id === filterId);
  if (result == null && throwError) {
    throw new Error(`Could not find filter with id=${filterId} and taxo=${type}`);
  }
  return result;
}

function onSearch(type: TaxonomyType, event: InputEvent) {
  const value = (event.target as HTMLInputElement)?.value ?? '';
  if (value.trim().length === 0) {
    search.value.delete(type);
  }
  else {
    search.value.set(type, value);
  }
}

async function update() {
  await fetchProductsList({
    size: 20,
    filters: configToFilter(config.value),
    properties: [],
    sorts: [],
    selections: [],
    metrics: [
      {
        type: 'taxonomyThemes',
      },
      {
        type: 'taxonomyAudiences',
      },
      {
        type: 'taxonomyInternals',
      },
      {
        type: 'taxonomyPlaces',
      },
      {
        type: 'taxonomyTypes',
      },
    ],
  });
}

onMounted(() => {
  update();
});

// Xavier Hernandez - Disabled updating filters based on client ticket
// watch(config, () => {
//   window.clearTimeout(debounce);
//   debounce = window.setTimeout(update, 700);
// }, { deep: true });
</script>

<template>
  <div class="selection-configuration">
    <div class="summary">
      <div class="summary-header">
        <span>{{ $t('editor.selection.summary') }}</span>
        <span>{{ totalResults }} <span class="subtext">{{ $t('search.activities').toLowerCase() }}</span></span>
      </div>
      <div v-for="taxo in config.taxonomies" :key="taxo.type" class="summary-category">
        <span class="summary-category-title">{{ $t(`filters.${taxo.type.toLowerCase()}`, 2) }} :</span>
        <span v-for="filterId in taxo.value" :key="filterId" class="summary-category-filter">
          {{ getFilterText(filterId, taxo.type) }}
          <X class="close" @click="setFilter(filterId, taxo.type)" />
        </span>
      </div>
    </div>
    <div v-for="(_filters, category) in categories" :key="category" class="filter-category">
      <span>{{ $t(`filters.${category.toLowerCase()}`, 2) }}</span>
      <div class="search">
        <Search class="search-icon" />
        <input
          type="text" :placeholder="`Rechercher un ${$t(`filters.${category.toLowerCase()}`).toLowerCase()} ...`"
          :aria-label="$t('search.button')" @input="onSearch(category, $event as InputEvent)"
        >
      </div>
      <div class="filter-values">
        <div
          v-for="filter in _filters" :key="filter.id" role="checkbox" :aria-checked="isSelected(filter, category)"
          tabindex="0" class="checkbox-container" @click="setFilter(filter.id, category)"
          @keydown.enter="setFilter(filter.id, category)"
        >
          <div :id="filter.id" class="checkbox" :data-selected="isSelected(filter, category)">
            <Check class="check" />
          </div>
          <label :for="filter.id">{{ filter.name }} ({{ filter.count }})</label>
        </div>
        <div v-if="_filters.length === 0" class="no-result">
          Aucun résultat
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.selection-configuration {
  display: flex;
  flex-direction: column;
  gap: 3rem;
  padding: 5px;
}

.summary {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background: #f8f9fa;
  padding: 1rem;

  .summary-header {
    display: flex;
    justify-content: space-between;
    font-weight: bold;
    font-size: 1.2rem;

    .subtext {
      font-weight: 400;
    }
  }

  .summary-category {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 1rem;

    .summary-category-title {
      font-weight: bold;
      line-height: 1;
    }

    .summary-category-filter {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      background: #ecedee;
      padding: 0.5rem;
      border-radius: 5px;
      font-size: 0.8rem;
      line-height: 1;

      .close {
        --size: 0.8rem;
        width: var(--size);
        height: var(--size);
        cursor: pointer;
      }
    }
  }
}

.filter-category {
  display: flex;
  gap: 1rem;
  flex-direction: column;

  & > span {
    font-weight: bold;
    font-size: 1.1rem;
  }

  .filter-values {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem 1rem;
  }

  .no-result {
    display: flex;
    width: 100%;
  }

  .search {
    position: relative;
    display: flex;
    border: 1px solid #bbb;
    border-radius: 8px;

    .search-icon {
      position: absolute;
      top: 50%;
      left: 1.4rem;
      transform: translate(-50%, -50%);
      width: 1.3rem;
    }

    input {
      background: transparent;
      border: none;
      width: 100%;
      min-height: 2.5rem;
      color: black;
      font-size: 0.9rem;
      padding-left: 3rem;
      border-radius: 8px;
      outline: none;
    }

    input:focus {
      outline: 2px solid var(--yb-color-primary);
    }

    input::placeholder {
      color: #aaa;
    }
  }
}

.checkbox-container {
  display: flex;
  align-items: center;
  gap: 0.3rem;

  .checkbox {
    position: relative;
    --size: 0.7rem;
    min-width: var(--size);
    max-width: var(--size);
    min-height: var(--size);
    max-height: var(--size);
    background: white;
    border: 1px solid #bbb;
    border-radius: 2px;
  }

  .checkbox[data-selected='true'] {
    background: var(--yb-color-primary);
    border-color: var(--yb-color-primary);
  }

  .check {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: white;
    width: 0.7rem;
  }
}
</style>
